import React from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  Clock,
  XCircle,
  User,
  Store,
  MapPin
} from 'lucide-react';
import { useWallet } from '../../../../context/wallet/WalletContext';
import {
  getStatusColor,
  getBusinessTitle,
  getBusinessLocation,
  extractBookingId
} from '../../utils/walletHelpers';
import { formatDate, format24HourTime } from '../../../../utils/formatters';

const getStatusIcon = (status) => {
  switch (status?.toUpperCase()) {
    case 'SUCCESS':
    case 'COMPLETED':
    case 'PROCESSED':
      return <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />;
    case 'PENDING':
    case 'PROCESSING':
      return <Clock size={13} className="text-amber-600 shrink-0" />;
    case 'FAILED':
      return <XCircle size={13} className="text-rose-600 shrink-0" />;
    default:
      return null;
  }
};

const WalletTransactionRow = ({ tx }) => {
  const { expandedTxId, toggleExpandTx, bookings } = useWallet();

  const isCredit = tx.type === 'CREDIT';
  const isExpanded = expandedTxId === tx._id;
  const bookingId = extractBookingId(tx.description, tx.referenceId);
  const booking = bookings.find((b) => b.booking_id === bookingId || b._id === tx.referenceId);

  return (
    <React.Fragment>
      <tr
        onClick={() => toggleExpandTx(tx._id)}
        className={`border-b border-slate-100 transition-colors text-sm cursor-pointer ${
          isExpanded ? 'bg-teal-50/40' : 'hover:bg-slate-50/70'
        }`}
      >
        <td className="p-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 border shadow-2xs ${
                isCredit
                  ? 'bg-emerald-50 border-emerald-100 text-emerald-600'
                  : 'bg-rose-50 border-rose-100 text-rose-600'
              }`}
            >
              {isCredit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
            </div>
            <div>
              <span className="font-semibold text-slate-900 text-xs sm:text-sm block">
                {isCredit ? 'Commission Earned' : 'Wallet Withdrawal'}
              </span>
              <span
                className={`inline-block text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md mt-0.5 ${
                  isCredit ? 'bg-emerald-100/60 text-emerald-800' : 'bg-slate-100 text-slate-700'
                }`}
              >
                {isCredit
                  ? 'Booking Commission'
                  : tx.description?.toLowerCase().includes('bank')
                  ? 'Bank Transfer'
                  : 'UPI Instant'}
              </span>
            </div>
          </div>
        </td>

        <td className="p-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-xs text-slate-700 font-medium">
              {tx.description || (isCredit ? 'Partner Booking Commission' : 'Wallet Redemption')}
            </span>
            {isCredit && (
              <span className="text-[11px] text-[#067978] font-mono lowercase">
                booking id: {bookingId}
              </span>
            )}
            {!isCredit && tx.razorpayPayoutId && (
              <span className="text-[11px] text-slate-400 font-mono">
                payout id: {tx.razorpayPayoutId}
              </span>
            )}
          </div>
        </td>

        <td className="p-4 text-slate-600 text-xs font-normal whitespace-nowrap">
          {formatDate(tx.createdAt)} {format24HourTime(tx.createdAt)}
        </td>

        <td className="p-4 whitespace-nowrap">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
              tx.status
            )}`}
          >
            {getStatusIcon(tx.status)}
            <span className="capitalize">{tx.status || 'Completed'}</span>
          </span>
        </td>

        <td className="p-4 text-right whitespace-nowrap">
          <div className="flex items-center justify-end gap-2">
            <span className={`text-sm font-bold ${isCredit ? 'text-emerald-600' : 'text-slate-800'}`}>
              {isCredit ? '+' : '-'}₹{(tx.amount || 0).toLocaleString('en-IN')}
            </span>
            <div className="w-6 h-6 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center transition-transform">
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </div>
        </td>
      </tr>

      {/* Expanded Transaction Details Drawer */}
      {isExpanded && (
        <tr className="bg-teal-50/30 border-b border-teal-100/50">
          <td colSpan="5" className="p-5 sm:pl-16">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs animate-in fade-in slide-in-from-top-1 duration-200">
              {isCredit ? (
                booking ? (
                  <>
                    {/* Guest & Partner Card */}
                    <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-2xs space-y-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Reservation Details
                      </span>
                      <div className="space-y-1.5 text-slate-700">
                        <div className="flex items-center gap-2">
                          <User size={13} className="text-[#067978]" />
                          <span>
                            Guest: <b>{booking.guest_name || booking.guestName || 'App User'}</b>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Store size={13} className="text-[#067978]" />
                          <span>
                            Partner: <b>{getBusinessTitle(booking)}</b>
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin size={13} className="text-[#067978]" />
                          <span>City: {getBusinessLocation(booking)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Timing & Type Card */}
                    <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-2xs space-y-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Trip &amp; Timing
                      </span>
                      <div className="space-y-1.5 text-slate-700">
                        <div>
                          Booking Type: <b className="capitalize">{booking.type}</b>
                        </div>
                        <div>
                          Date: <b>{formatDate(booking.date)}</b>
                        </div>
                        <div>
                          Booked Time: <b>{format24HourTime(booking.bookedTime) || 'N/A'}</b>
                        </div>
                      </div>
                    </div>

                    {/* Payout & Bill Card */}
                    <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-2xs space-y-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Financial Split
                      </span>
                      <div className="space-y-1.5 text-slate-700">
                        <div>Total Bill: ₹{(booking.final_bill_amount || 0).toLocaleString('en-IN')}</div>
                        <div>
                          Commission Yield:{' '}
                          <b className="text-emerald-600 font-bold">
                            +₹{(tx.amount || 0).toLocaleString('en-IN')}
                          </b>
                        </div>
                        <div>
                          Booking ID:{' '}
                          <span className="font-mono text-[#067978] lowercase">{booking.booking_id}</span>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-2xs space-y-2 md:col-span-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Transaction Details
                      </span>
                      <div className="space-y-1 text-slate-700">
                        <div>Description: {tx.description}</div>
                        <div>
                          Reference ID:{' '}
                          <span className="font-mono text-slate-500">{tx.referenceId || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-teal-100 shadow-2xs flex flex-col justify-center">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        Commission Credited
                      </span>
                      <div className="text-xl font-bold text-emerald-600 mt-1">
                        +₹{(tx.amount || 0).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </>
                )
              ) : (
                <>
                  {/* Debit / Payout details */}
                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Redemption Details
                    </span>
                    <div className="space-y-1 text-slate-700">
                      <div>
                        Debited Amount:{' '}
                        <b className="text-rose-600">-₹{(tx.amount || 0).toLocaleString('en-IN')}</b>
                      </div>
                      <div>
                        Status: <span className="uppercase font-semibold">{tx.status}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Payout Channel
                    </span>
                    <div className="space-y-1 text-slate-700">
                      <div>Method: {tx.description}</div>
                      {tx.razorpayPayoutId && (
                        <div>
                          Payout ID: <span className="font-mono text-slate-600">{tx.razorpayPayoutId}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-2xs space-y-2 flex flex-col justify-center">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                      Reference Token
                    </span>
                    <p className="font-mono text-slate-700 truncate">{tx.referenceId || 'N/A'}</p>
                  </div>
                </>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

export default WalletTransactionRow;
